import {useEffect, useState} from "react";
import {ChevronDown} from "lucide-react";
import {
    CATEGORY_GROUPS, CATEGORY_KEYS, CategoryGroup, STOCK_SECTORS, STOCK_SECTOR_KEYS,
    StockSector, categoryGroup, categoryPath, parseStockCategory, stockCategory,
} from "../global/categories";

type Props = {value: string | null; onChange: (category: string | null) => void};

export default function CategoryNavigation({value, onChange}: Props) {
    const [openGroup, setOpenGroup] = useState<CategoryGroup | null>(null);
    const [openSector, setOpenSector] = useState<StockSector | null>(null);
    const selectedStock = parseStockCategory(value);

    useEffect(() => {
        if (value === null) {
            setOpenGroup(null);
            setOpenSector(null);
        }
    }, [value]);

    const clear = () => {
        setOpenGroup(null);
        setOpenSector(null);
        onChange(null);
    };

    return <section className="category-area" aria-labelledby="category-heading">
        <h2 id="category-heading" className="category-heading">주제별 팩트</h2>
        <div className="category-nav">
            <button type="button" className="category-group-button" aria-pressed={value === null} onClick={clear}>전체</button>
            {CATEGORY_KEYS.map((group, index) => (
                <button type="button" key={group} id={`category-group-${index}`}
                    className={`category-group-button ${categoryGroup(value) === group ? "has-selection" : ""}`}
                    aria-expanded={openGroup === group} aria-controls={`category-panel-${index}`}
                    onClick={() => setOpenGroup(openGroup === group ? null : group)}>
                    {group}<ChevronDown className="category-chevron" size={16} strokeWidth={1.8} aria-hidden="true" />
                </button>
            ))}
        </div>
        {CATEGORY_KEYS.map((group, index) => (
            <div key={group} id={`category-panel-${index}`} role="group"
                className="category-group-items" aria-labelledby={`category-group-${index}`} hidden={openGroup !== group}>
                {group === "주식" ? <>
                    <button type="button" className="category-item" aria-pressed={value === "주식"}
                        onClick={() => {setOpenSector(null); onChange("주식");}}>주식 전체</button>
                    {STOCK_SECTOR_KEYS.map((sector, sectorIndex) => (
                        <button type="button" key={sector} id={`stock-sector-${sectorIndex}`}
                            className={`category-item category-sector ${selectedStock?.sector === sector ? "has-selection" : ""}`}
                            aria-expanded={openSector === sector} aria-controls={`stock-industries-${sectorIndex}`}
                            onClick={() => setOpenSector(openSector === sector ? null : sector)}>
                            {sector}<ChevronDown className="category-chevron" size={16} strokeWidth={1.8} aria-hidden="true" />
                        </button>
                    ))}
                    {STOCK_SECTOR_KEYS.map((sector, sectorIndex) => (
                        <div key={sector} id={`stock-industries-${sectorIndex}`} className="category-industries"
                            role="group" aria-labelledby={`stock-sector-${sectorIndex}`} hidden={openSector !== sector}>
                            <p className="category-industry-heading">주식 › {sector}</p>
                            <div className="category-industry-options">
                                <button type="button" className="category-item" aria-pressed={value === stockCategory(sector)}
                                    onClick={() => onChange(stockCategory(sector))}>섹터 전체</button>
                                {STOCK_SECTORS[sector].map(industry => (
                                    <button type="button" key={industry} className="category-item"
                                        aria-pressed={value === stockCategory(sector, industry)}
                                        onClick={() => onChange(stockCategory(sector, industry))}>
                                        {value === stockCategory(sector, industry) && <span aria-hidden="true">✓ </span>}{industry}
                                    </button>
                                ))}
                            </div>
                        </div>
                    ))}
                </> : CATEGORY_GROUPS[group].map(category => (
                    <button type="button" key={category} className="category-item" aria-pressed={value === category}
                        onClick={() => onChange(category)}>
                        {value === category && <span aria-hidden="true">✓ </span>}{category}
                    </button>
                ))}
            </div>
        ))}
        {value && <div className="category-selection">
            <span>선택한 주제: <strong>{categoryPath(value)}</strong></span>
            <button type="button" onClick={clear}>필터 해제</button>
        </div>}
    </section>;
}
